import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/**
 * "Connect GitHub" OAuth flow (no React in here — all logic is testable).
 *
 * GitHub's token endpoints have no CORS and require the client secret, so
 * the code→token exchange happens on a tiny server route
 * (GH_OAUTH_EXCHANGE_URL; dev shim in apps/evidence-viewer). Everything
 * else is client-side: the user's token lives in THIS browser's
 * localStorage and artifact fetches go browser → api.github.com directly.
 *
 * Round-trip: beginGhLogin stashes {nonce, url} in sessionStorage and
 * navigates to GitHub's consent page; GitHub returns to the origin with
 * ?code&state (or ?error); handleOAuthReturn validates the nonce,
 * exchanges the code, stores the token, and tells the caller to
 * location.replace the original URL — one clean re-navigation that boots
 * the viewer with the token present and every deep-link param intact.
 */

import { GH_AUTHORIZE_URL, GH_OAUTH_CLIENT_ID, GH_OAUTH_EXCHANGE_URL, GH_OAUTH_SCOPE } from '../config';
import { PackError } from '../pack/errors';
export var GH_TOKEN_KEY = 'ev-gh-token';
export var GH_OAUTH_STASH_KEY = 'ev-gh-oauth';
export var GH_DECLINED_KEY = 'ev-gh-declined';

/* ── token storage (try/catch accessors, app/internal.ts pattern) ── */

export function getGhToken() {
  try {
    return window.localStorage.getItem(GH_TOKEN_KEY);
  } catch (_unused) {
    return null;
  }
}
export function setGhToken(token) {
  try {
    window.localStorage.setItem(GH_TOKEN_KEY, token);
  } catch (_unused2) {
    /* storage unavailable — the session just stays gateway-gated */
  }
}
export function clearGhToken() {
  try {
    window.localStorage.removeItem(GH_TOKEN_KEY);
  } catch (_unused3) {
    /* nothing to clear */
  }
}

/* ── the outbound leg ── */

function randomNonce() {
  try {
    var bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, function (b) {
      return b.toString(16).padStart(2, '0');
    }).join('');
  } catch (_unused4) {
    // No WebCrypto (ancient env) — still unguessable enough for CSRF.
    return "".concat(Date.now().toString(36), "-").concat(Math.random().toString(36).slice(2));
  }
}

/** Stash the return URL + CSRF nonce and navigate to GitHub's consent
 * page. No-op when no client id is configured. */
export function beginGhLogin(returnUrl) {
  if (!GH_OAUTH_CLIENT_ID) return;
  var nonce = randomNonce();
  try {
    window.sessionStorage.setItem(GH_OAUTH_STASH_KEY, JSON.stringify({
      nonce: nonce,
      url: returnUrl
    }));
  } catch (_unused5) {
    return; // without the stash the return leg can't be validated
  }
  var u = new URL(GH_AUTHORIZE_URL);
  u.searchParams.set('client_id', GH_OAUTH_CLIENT_ID);
  u.searchParams.set('redirect_uri', "".concat(window.location.origin, "/"));
  u.searchParams.set('scope', GH_OAUTH_SCOPE);
  u.searchParams.set('state', nonce);
  window.location.assign(u.toString());
}

/* ── the return leg ── */

/** Non-null when the search string looks like a GitHub OAuth return:
 * `state` plus either `code` (granted) or `error` (declined). */
export function ghOAuthParamsFromSearch(search) {
  var p = new URLSearchParams(search);
  var state = p.get('state');
  var code = p.get('code');
  var error = p.get('error');
  if (!state || !code && !error) return null;
  return {
    code: code,
    error: error,
    state: state
  };
}

/** Validate the CSRF nonce against the stash; consume it either way.
 * Returns the original URL to restore, or null on mismatch/absence. */
export function consumeOAuthStash(state) {
  try {
    var raw = window.sessionStorage.getItem(GH_OAUTH_STASH_KEY);
    window.sessionStorage.removeItem(GH_OAUTH_STASH_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (typeof parsed.nonce !== 'string' || typeof parsed.url !== 'string') {
      return null;
    }
    return parsed.nonce === state ? parsed.url : null;
  } catch (_unused6) {
    return null;
  }
}
export function setDeclinedFlag() {
  try {
    window.sessionStorage.setItem(GH_DECLINED_KEY, '1');
  } catch (_unused7) {
    /* cosmetic only */
  }
}

/** One-shot: true when the user just declined the GitHub consent. */
export function consumeDeclinedFlag() {
  try {
    var v = window.sessionStorage.getItem(GH_DECLINED_KEY) === '1';
    window.sessionStorage.removeItem(GH_DECLINED_KEY);
    return v;
  } catch (_unused8) {
    return false;
  }
}

/** Server-side code→token exchange. Throws PackError('gh-auth-required')
 * on any failure — callers surface a retry, never a raw error. */
export function exchangeCode(_x, _x2) {
  return _exchangeCode.apply(this, arguments);
}

/** Remove exactly the OAuth params — url-sync preserves unknown params
 * forever, so a leftover ?code&state would leak into copied links. */
function _exchangeCode() {
  _exchangeCode = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(code, opts) {
    var _opts$fetchImpl, _opts$endpoint;
    var fetchImpl, endpoint, res, body, _t, _t2;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          fetchImpl = (_opts$fetchImpl = opts === null || opts === void 0 ? void 0 : opts.fetchImpl) !== null && _opts$fetchImpl !== void 0 ? _opts$fetchImpl : fetch.bind(globalThis);
          endpoint = (_opts$endpoint = opts === null || opts === void 0 ? void 0 : opts.endpoint) !== null && _opts$endpoint !== void 0 ? _opts$endpoint : GH_OAUTH_EXCHANGE_URL;
          _context.prev = 1;
          _context.next = 2;
          return fetchImpl(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              code: code
            })
          });
        case 2:
          res = _context.sent;
          _context.next = 4;
          break;
        case 3:
          _context.prev = 3;
          _t = _context["catch"](1);
          throw new PackError('gh-auth-required', "token exchange unreachable: ".concat(_t instanceof Error ? _t.message : String(_t)));
        case 4:
          body = {};
          _context.prev = 5;
          _context.next = 6;
          return res.json();
        case 6:
          body = _context.sent;
          _context.next = 8;
          break;
        case 7:
          _context.prev = 7;
          _t2 = _context["catch"](5);
        case 8:
          if (!(!res.ok || typeof body.token !== 'string' || body.token === '')) {
            _context.next = 9;
            break;
          }
          throw new PackError('gh-auth-required', "token exchange failed: ".concat(typeof body.error === 'string' ? body.error : "HTTP ".concat(res.status)));
        case 9:
          return _context.abrupt("return", body.token);
        case 10:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 3], [5, 7]]);
  }));
  return _exchangeCode.apply(this, arguments);
}
export function stripOAuthParams() {
  try {
    var url = new URL(window.location.href);
    url.searchParams["delete"]('code');
    url.searchParams["delete"]('state');
    url.searchParams["delete"]('error');
    url.searchParams["delete"]('iss'); // GitHub appends an issuer param on return
    window.history.replaceState(window.history.state, '', url);
  } catch (_unused9) {
    /* no history API — nothing to strip */
  }
}
/**
 * Orchestrate the OAuth return. The component around this only renders a
 * spinner and acts on the outcome:
 *  - redirect        → location.replace(url)  (token stored, or declined-flag set)
 *  - anomaly         → stripOAuthParams() and render the plain viewer
 *    (nonce mismatch / bookmarked callback — do nothing exciting)
 *  - exchange-failed → error card with a retry back to retryUrl
 */
export function handleOAuthReturn(_x3, _x4) {
  return _handleOAuthReturn.apply(this, arguments);
}
function _handleOAuthReturn() {
  _handleOAuthReturn = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(params, opts) {
    var stashedUrl, token, _t3;
    return _regeneratorRuntime.wrap(function (_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          stashedUrl = consumeOAuthStash(params.state);
          if (!(stashedUrl === null)) {
            _context2.next = 1;
            break;
          }
          return _context2.abrupt("return", {
            kind: 'anomaly'
          });
        case 1:
          if (!(params.error || !params.code)) {
            _context2.next = 2;
            break;
          }
          setDeclinedFlag();
          return _context2.abrupt("return", {
            kind: 'redirect',
            url: stashedUrl
          });
        case 2:
          _context2.prev = 2;
          _context2.next = 3;
          return exchangeCode(params.code, opts);
        case 3:
          token = _context2.sent;
          setGhToken(token);
          return _context2.abrupt("return", {
            kind: 'redirect',
            url: stashedUrl
          });
        case 4:
          _context2.prev = 4;
          _t3 = _context2["catch"](2);
          return _context2.abrupt("return", {
            kind: 'exchange-failed',
            retryUrl: stashedUrl
          });
        case 5:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[2, 4]]);
  }));
  return _handleOAuthReturn.apply(this, arguments);
}